import { ReactNode, useMemo } from 'react';

import createSafeContext from '#/utils/createSafeContext';
import usePrevious from '#/utils/usePrevious';

import * as MarketFn from '#/features/market/functions';
import { useTradeActiveMarketStoreView } from '#/features/perpetuals/active-market-context';

import { Trade, TradeSide } from '#/api/trades';
import { Market } from '#/api/markets';

import { useTradesStore } from './trades-store-context';

export interface LastTradeView {
  readonly id: string;
  readonly price: string;
  readonly direction: 'UP' | 'DOWN' | 'UNKNOWN';
  readonly side: TradeSide;
}

const EMPTY_VIEW: LastTradeView = {
  id: '0',
  price: '-',
  direction: 'UNKNOWN',
  side: 'BUY',
};

export const [useLastTradeView, LastTradeViewProvider] =
  createSafeContext<LastTradeView>('LastTradeView');

interface ProviderProps {
  readonly children: ReactNode;
}

export function LastTradeProvider(props: ProviderProps) {
  const tradesStore = useTradesStore();
  const { activeMarket } = useTradeActiveMarketStoreView();

  const currentLatestTrade = tradesStore.getLatestTrade();
  const previousLatestTraded = usePrevious(currentLatestTrade);

  const lastTradedPrice = useMemo(
    () => ({
      current: currentLatestTrade?.price,
      previous: previousLatestTraded?.price,
    }),
    // Memoize the string value so that the view is not updated when a new trade
    // with the same price comes. Memoizing the string value is necessary because
    // price is a BigNumber and for each trade the BigNumber instance is different.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLatestTrade?.price.toString()],
  );

  const view = useMemo(
    () =>
      createView({
        lastTradeId: currentLatestTrade?.id,
        lastTradedPrice,
        side: currentLatestTrade?.side,
        market: activeMarket,
      }),
    [
      currentLatestTrade?.id,
      currentLatestTrade?.side,
      lastTradedPrice,
      activeMarket,
    ],
  );

  return (
    <LastTradeViewProvider value={view}>{props.children}</LastTradeViewProvider>
  );
}

interface CreateViewOptions {
  readonly lastTradeId: string | undefined;
  readonly lastTradedPrice: {
    readonly current: Trade['price'] | undefined;
    readonly previous: Trade['price'] | undefined;
  };
  readonly side: Trade['side'] | undefined;
  readonly market: Market | null;
}

export function createView(options: CreateViewOptions): LastTradeView {
  if (options.market == null) return EMPTY_VIEW;
  if (options.lastTradeId == null) return EMPTY_VIEW;
  if (options.lastTradedPrice.current == null) return EMPTY_VIEW;
  if (options.side == null) return EMPTY_VIEW;

  return {
    id: options.lastTradeId,
    price: priceView(options.lastTradedPrice.current, options.market),
    direction: directionView(
      options.lastTradedPrice.current,
      options.lastTradedPrice.previous,
    ),
    side: options.side,
  };
}

function priceView(
  currentLatestTradedPrice: Trade['price'],
  market: Market,
): LastTradeView['price'] {
  const quoteCurrencyDecimalPlaces =
    MarketFn.quoteCurrencyDecimalPlaces(market);
  const price = currentLatestTradedPrice.toFormat(quoteCurrencyDecimalPlaces);
  return price;
}

function directionView(
  currentLatestTradedPrice: Trade['price'],
  previousLatestTradedPrice: Trade['price'] | undefined,
): LastTradeView['direction'] {
  if (previousLatestTradedPrice == null) return 'UNKNOWN';
  if (currentLatestTradedPrice.isGreaterThan(previousLatestTradedPrice))
    return 'UP';
  if (currentLatestTradedPrice.isLessThan(previousLatestTradedPrice))
    return 'DOWN';
  return 'UNKNOWN';
}
